import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {boolean} */
  #pendingAutoOpen = false;

  /** @type {boolean} */
  #userDismissedDuringAdd = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
  }

  /**
   * Tracks manual drawer dismissal while an add-to-cart auto-open is pending.
   * @param {Event} event
   */
  #handleDrawerClose = (event) => {
    if (event.target !== this.#themeDrawer) return;
    if (this.#pendingAutoOpen) {
      this.#userDismissedDuringAdd = true;
    }
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    requestAnimationFrame(() => setTimeout(() => this.#updateStickyState(), 0));

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;
    const wasEmptyBeforeAdd = this.#isCartEmpty();

    if (shouldAutoOpen) {
      this.#userDismissedDuringAdd = false;
      this.#pendingAutoOpen = true;
    }

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = (stickyHandledByOpen = false) => {
          this.#syncFilledCartState();
          if (!stickyHandledByOpen) requestAnimationFrame(() => this.#updateStickyState());
        };

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          const openDrawer = () => {
            let opened = false;
            if (!this.#themeDrawer?.isOpen && !this.#userDismissedDuringAdd) {
              this.#syncFilledCartState();
              this.#themeDrawer?.open();
              opened = true;
            }
            settle(opened);
          };

          if (wasEmptyBeforeAdd) {
            this.#waitForFilledDrawer()
              .then(() => new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0))))
              .then(openDrawer);
            return;
          }

          openDrawer();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      })
      .finally(() => {
        this.#pendingAutoOpen = false;
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('[data-cart-drawer-empty], .cart-drawer--empty'));
  }

  #syncFilledCartState() {
    const hasEmptyMarker = Boolean(this.querySelector('[data-cart-drawer-empty]'));
    const hasLineItems = Boolean(
      this.querySelector('.am-cart-product-card[data-key], .cart-items__table-row[data-key]')
    );

    if (hasEmptyMarker) {
      this.#dialog?.classList.add('cart-drawer--empty');
      this.querySelector('.cart-drawer__inner')?.classList.add('cart-drawer--empty');
      return;
    }

    if (!hasLineItems) return;

    this.#dialog?.classList.remove('cart-drawer--empty');
    this.querySelector('.cart-drawer__inner')?.classList.remove('cart-drawer--empty');
  }

  /**
   * Clears empty-drawer layout classes after external cart fills.
   */
  syncFilledCartState() {
    this.#syncFilledCartState();
  }

  /**
   * @returns {Promise<void>}
   */
  #waitForFilledDrawer() {
    return new Promise((resolve) => {
      /** @type {FrameRequestCallback} */
      const check = () => {
        if (!this.querySelector('[data-cart-drawer-empty]')) {
          resolve();
          return;
        }

        requestAnimationFrame(check);
      };

      check();
    });
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
